import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGlobalSettings } from "@/hooks/useGlobalSettings";
import { cn } from "@/lib/utils";

interface MiniCtaSectionProps {
  title?: string;
  ctaText?: string;
  ctaLink?: string;
  showPhone?: boolean;
  variant?: 'default' | 'accent' | 'subtle';
  className?: string;
}

const MiniCtaSection = ({
  title = "Ready to get started?",
  ctaText = "Get a Free Quote",
  ctaLink = "/quote",
  showPhone = true,
  variant = 'default',
  className,
}: MiniCtaSectionProps) => {
  const { data: settings } = useGlobalSettings();
  const phone = settings?.phone;
  const phoneLink = phone ? `tel:${phone.replace(/\s/g, "")}` : null;

  const variants = {
    default: "bg-primary text-primary-foreground",
    accent: "bg-accent text-accent-foreground",
    subtle: "bg-muted text-foreground",
  };

  return (
    <section className={cn("py-8", variants[variant], className)}>
      <div className="container-wide">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-lg font-heading font-semibold text-center sm:text-left">
            {title}
          </p>
          
          <div className="flex items-center gap-3">
            {showPhone && phoneLink && (
              <Button
                asChild
                variant={variant === 'subtle' ? 'outline' : 'secondary'}
                size="sm"
                className="gap-2"
              >
                <a href={phoneLink}>
                  <Phone className="h-4 w-4" />
                  <span className="hidden sm:inline">{phone}</span>
                  <span className="sm:hidden">Call</span>
                </a>
              </Button>
            )}
            
            <Button
              asChild
              variant={variant === 'accent' ? 'secondary' : 'default'}
              size="sm"
              className={cn(
                "gap-2 group",
                variant === 'default' && "bg-accent text-accent-foreground hover:bg-accent/90"
              )}
            >
              <Link to={ctaLink}>
                {ctaText}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MiniCtaSection;
